import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { listSceneVideos } from '../../../features/scenarios/api';
import { requestDeleteTemporary } from '../../../features/materials/api';
import { useLockScrollOutside } from '../../../hooks/useLockScrollOutside';
import styles from './ScenarioGenerator.module.css';
import type { GenerationModel } from '../../../features/generation-models/api';
import { MOTION_MODEL_LABELS, type MotionModel } from '../../../features/motion/model';

/**
 * 動画履歴行の AI モデル表記 (ナレーション履歴の「n秒（プロバイダ）」と同じ体系)。
 * generation_models の model_family_name + display_label (例 'Seedance 2.5 標準' /
 * 'Gemini Omni Flash スピード')。display_label がファミリー名の末尾と重なる場合
 * (MiniMax H3 / 'H3') は重ねない。一覧に無いモデル (廃止済み等) は model_code をそのまま出す。
 * モーショングラフィックス (engine='motion') は初稿を書いた AI モデル (model) の表示名 (例 'Gemini 4 Argon')。
 */
export function videoTakeModelLabel(
  models: GenerationModel[] | undefined,
  videoModel?: string | null,
  model?: string | null,
  engine?: string | null,
): string | undefined {
  if (engine === 'motion') {
    if (!model) return 'モーショングラフィックス';
    return MOTION_MODEL_LABELS[model as MotionModel] ?? model;
  }
  const code = videoModel || model;
  if (!code) return undefined;
  const spec = (models ?? []).find((m) => m.model_code === code);
  if (!spec) return code;
  const family = spec.model_family_name || spec.display_label;
  const tier = spec.display_label;
  if (!spec.model_family_name || !tier || family.endsWith(tier)) return family;
  return `${family} ${tier}`;
}

/** 動画履歴セレクタ */
export function VideoHistorySelector({
  sceneId,
  currentMaterialId,
  onSelect,
  excluded,
  onToggleExcluded,
  videoModels,
  kind,
  disabled,
}: {
  sceneId: string;
  currentMaterialId?: string;
  /** 'motion_overlay' = 絵コンテのモーション上書きの過去版 (generation_metadata.motion_overlay の候補)。
   *  使用中の版は消せない (上書きの行が素材を指したまま残るため) */
  kind?: 'motion_overlay';
  disabled?: boolean;
  onSelect: (materialId: string, durationSeconds?: number, hasAudio?: boolean) => void;
  /** 履歴行のモデル名解決用 (useGenerationModels('video') の一覧)。未指定なら model_code を出す */
  videoModels?: GenerationModel[];
  /** 「採用しない」トグル (プレビュー・エクスポートから除外)。ポップアップ最上部に行を出す */
  excluded?: boolean;
  onToggleExcluded?: (excluded: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [videos, setVideos] = useState<Array<{
    id: string;
    created_at: string;
    is_current: boolean;
    duration_seconds?: number;
    has_audio?: boolean;
    scene_range?: { start: number; end: number } | null;
    video_model?: string | null;
    model?: string | null;
    engine?: string | null;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const [currentDuration, setCurrentDuration] = useState<number | undefined>(undefined);
  const [currentTakeNumber, setCurrentTakeNumber] = useState<number | undefined>(undefined);
  const [currentSceneRange, setCurrentSceneRange] = useState<{ start: number; end: number } | null | undefined>(undefined);

  // 「特定シーンだけ生成」のテイクは範囲付きラベルにする。範囲なしは従来通り。
  const takeLabel = (idx: number, range?: { start: number; end: number } | null) => {
    if (range && range.start != null && range.end != null) {
      const span = range.start === range.end ? `シーン${range.start}` : `シーン${range.start}-${range.end}`;
      return `テイク${idx + 1}（${span}）`;
    }
    return `テイク${idx + 1}`;
  };

  // この画面で選んだ素材 (選んだ結果 currentMaterialId が変わってもテイク番号の表示は残す)
  const selectedRef = useRef<string | undefined>(undefined);

  // currentMaterialIdが外部から変更された場合にキャッシュをリセット
  useEffect(() => {
    if (currentMaterialId && currentMaterialId === selectedRef.current) return;
    setVideos([]);
    setCurrentTakeNumber(undefined);
    setCurrentSceneRange(undefined);
  }, [currentMaterialId]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // ポップアップは開いた位置に fixed で置くため、開いている間は背景を動かさない
  useLockScrollOutside(open, dropdownRef);

  const updateDropdownPos = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.top, left: rect.left });
    }
  };

  const fetchHistory = async () => {
    if (open) { setOpen(false); return; }
    setLoading(true);
    try {
      const items = (await listSceneVideos(sceneId, kind ? { kind } : undefined)).reverse();
      setVideos(items);
      if (currentMaterialId) {
        const currentIdx = items.findIndex((v: { id: string }) => v.id === currentMaterialId);
        if (currentIdx >= 0) {
          const current = items[currentIdx];
          if (current.duration_seconds != null) setCurrentDuration(current.duration_seconds);
          setCurrentTakeNumber(currentIdx + 1);
          setCurrentSceneRange(current.scene_range ?? null);
        }
      }
      updateDropdownPos();
      setOpen(true);
    } catch {
      // 取得失敗
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (materialId: string) => {
    try {
      await requestDeleteTemporary(materialId);
      setVideos(prev => prev.filter(v => v.id !== materialId));
    } catch {
      // 削除失敗
    }
  };

  const dropdownContent = (items: typeof videos) => (
    <div
      ref={dropdownRef}
      className={`modal-surface ${styles.narrationHistoryDropdown}`}
      style={{
        position: 'fixed',
        bottom: `${window.innerHeight - dropdownPos.top + 4}px`,
        left: `${dropdownPos.left}px`,
      }}
    >
      {/* 最上部: 「採用しない」行 (右寄せ・非活性色)。テイクは保持したままプレビュー
          MP4・エクスポートから外す。採用に戻すUIは無く、いずれかのテイクを選ぶと戻る。 */}
      {onToggleExcluded && currentMaterialId && !excluded && (
        <div
          className={styles.narrationHistoryItem}
          style={{ borderBottom: 'var(--modal-divider, 1px solid rgba(255, 255, 255, 0.08))' }}
        >
          <button
            type="button"
            className={styles.narrationHistorySelect}
            style={{ justifyContent: 'flex-end', color: 'rgba(245, 241, 230, 0.45)' }}
            onClick={() => {
              onToggleExcluded(true);
              setOpen(false);
            }}
            title="プレビュー動画やエクスポートに含めません。テイクを選ぶと採用に戻ります"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              採用しない
            </span>
          </button>
        </div>
      )}
      {items.length > 0 ? items.map((v, idx) => (
        <div
          key={v.id}
          className={`${styles.narrationHistoryItem} ${v.id === currentMaterialId ? styles.narrationHistoryCurrent : ''}`}
        >
          <button
            type="button"
            className={styles.narrationHistorySelect}
            onClick={() => {
              selectedRef.current = v.id;
              onSelect(v.id, v.duration_seconds, v.has_audio);
              if (v.duration_seconds != null) setCurrentDuration(v.duration_seconds);
              setCurrentTakeNumber(idx + 1);
              setCurrentSceneRange(v.scene_range ?? null);
              setOpen(false);
            }}
          >
            <span className={styles.narrationHistoryInfo}>
              <span>{takeLabel(idx, v.scene_range)}</span>
              {v.duration_seconds != null && (
                // probe 由来の実数尺 (例 15.04) は秒単位に丸めて表示する。
                // モデル名はナレーション履歴と同じ体系「n秒（ファミリー モデル名）」
                <span className={styles.narrationHistoryDuration}>
                  {Math.round(v.duration_seconds)}秒
                  {(() => {
                    const label = videoTakeModelLabel(videoModels, v.video_model, v.model, v.engine);
                    return label ? `（${label}）` : '';
                  })()}
                </span>
              )}
              {v.has_audio ? (
                <svg className={styles.videoHistoryAudioIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.08"/></svg>
              ) : (
                <svg className={styles.videoHistoryMutedIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
              )}
            </span>
            {v.id === currentMaterialId && <span className={styles.narrationHistoryBadge}>選択中</span>}
          </button>
          {!(kind === 'motion_overlay' && v.id === currentMaterialId) && (
            <button
              type="button"
              className={styles.narrationHistoryDelete}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(v.id);
              }}
              title="削除"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M5.5 1.5h5M2.5 4h11M6 4v8.5M10 4v8.5M3.5 4l.5 10.5a1 1 0 001 .5h6a1 1 0 001-.5L12.5 4" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      )) : (
        <div className={styles.narrationHistoryItem} style={{ cursor: 'default', opacity: 0.6 }}>
          履歴がありません
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: 'inline-block' }}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.narrationHistoryButton}
        onClick={fetchHistory}
        disabled={loading || disabled}
        title={kind === 'motion_overlay' ? '過去に生成したモーション' : '過去の動画履歴'}
      >
        {loading ? (
          <span className={styles.spinnerSmall} />
        ) : (
          <>
            {excluded ? (
              <span className={styles.narrationHistoryDurationLabel}>採用なし</span>
            ) : (
              <>
                {currentTakeNumber != null && <span className={styles.narrationHistoryDurationLabel}>{takeLabel(currentTakeNumber - 1, currentSceneRange)}</span>}
                {currentDuration != null && <span className={styles.narrationHistoryDurationLabel}>{Math.round(currentDuration)}秒</span>}
              </>
            )}
            {(videos.length === 0 || videos.length > 1 || excluded) && <span className={styles.narrationHistoryCaret}>&#9662;</span>}
          </>
        )}
      </button>
      {open && createPortal(dropdownContent(videos), document.body)}
    </div>
  );
}
